import { useMemo } from 'react';
import DataTable from '@/enterprise/components/DataTable/DataTable';
import {
  getNewDivisionAlertEligibleTakerMarkets,
  useEnableNewDivisionAlert,
} from '@/enterprise/components/NewDivisionAlert';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import useGetRecurringRulesForTakerMarkets from '@/enterprise/features/recurringRules/utils/useGetRecurringRulesForTakerMarkets';
import { IntercomDataTargetNames } from '@/reporting/intercomDataTargetNames';
import useColumns from './columns';

interface MarketTableProps {
  // whether this is the name your rate markets table or not
  isNameYourRate?: boolean;
  // whether to include next market clear time countdown
  includeNextMarketClearTimeCountdown?: boolean;
  // where the table is being used, defaults to 'markets'
  referrer?: 'customers' | 'markets';
  // taker markets to display
  takerMarkets: TakerMarket[];
}

const MarketTable = ({
  includeNextMarketClearTimeCountdown = true,
  isNameYourRate = false,
  referrer = 'markets',
  takerMarkets,
}: MarketTableProps) => {
  const { getRecurringRulesForTakerMarkets } = useGetRecurringRulesForTakerMarkets();
  const hasPreferredTakerMarkets = useMemo(
    () => takerMarkets.some((takerMarket) => takerMarket.offerConfig.offerType === 'PREFERRED_TERM'),
    [takerMarkets]
  );
  const enableNewDivisionAlert = useEnableNewDivisionAlert();
  const getNewDivisionAlertEligible = enableNewDivisionAlert ? getNewDivisionAlertEligibleTakerMarkets : undefined;
  // show term end column if there are preferred taker markets
  const showTermEndColumn = hasPreferredTakerMarkets;
  // show excluded column always on customers table OR on markets table if there are no preferred taker markets
  const showExcludedColumn = referrer === 'customers' || (referrer === 'markets' && !hasPreferredTakerMarkets);
  // show buyer info on markets table
  const showBuyerInfo = referrer === 'markets';
  const { firstMarketWithRecurringRulesIndex, firstMarketWithScheduleIndex } = useMemo(
    () => ({
      firstMarketWithRecurringRulesIndex: takerMarkets.findIndex(
        (tm) => getRecurringRulesForTakerMarkets([tm]).length > 0
      ),
      firstMarketWithScheduleIndex: takerMarkets.findIndex((tm) => !!tm.offerSchedule),
    }),
    [takerMarkets, getRecurringRulesForTakerMarkets]
  );

  const columns = useColumns({
    firstMarketWithRecurringRulesIndex,
    showBuyerInfo,
    firstMarketWithScheduleIndex,
    includeNextMarketClearTimeCountdown,
  });

  return (
    <div className="border-t border-stroke">
      <DataTable
        columns={columns}
        data={takerMarkets}
        initialState={{
          sorting: [
            {
              id: 'totalInvoiceAmount',
              desc: true,
            },
          ],
        }}
        state={{
          columnPinning: {
            right: ['actions'],
          },
          columnVisibility: {
            disableAfterMarketClearsDate: showTermEndColumn,
            takerExcludedInvoiceAmount: showExcludedColumn,
            marketPayDate: false,
            marketGoodFundsDate: true,
          },
        }}
        getRowProps={(takerMarket, index) =>
          index === 0 && isNameYourRate
            ? { 'data-intercom-target': 'firstEligibleDivision' satisfies IntercomDataTargetNames }
            : getNewDivisionAlertEligible?.([takerMarket]).includes(takerMarket)
            ? { className: 'border-l-[4px] border-l-info-500' }
            : {}
        }
      />
    </div>
  );
};

export default MarketTable;
